import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/axios';
import './ClientContactSelection.css';

const ClientContactSelection = ({ onComplete, initialClientName, initialCustomerFromRequest, onBack }) => {
  const { user, selectedSubscriberId } = useAuth();
  
  // State for klanten (customers from Odoo - res.partner, the 204)
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [loadingCustomers, setLoadingCustomers] = useState(true);
  
  // Search modal (zoek klant in venster)
  const [searchModalOpen, setSearchModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchDebounceRef = useRef(null);
  
  // State for contacts (from Odoo - under selected klant)
  const [contacts, setContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [loadingContacts, setLoadingContacts] = useState(false);
  
  // State for new klant/contact form
  const [isNewCustomer, setIsNewCustomer] = useState(false);
  const [isNewContact, setIsNewContact] = useState(false);
  
  // New klant form data (customerType: 'bedrijf' | 'particulier')
  const [newCustomerData, setNewCustomerData] = useState({
    customerType: 'bedrijf',
    name: '',
    email: '',
    phone: '',
    street: '',
    city: '',
    zip: '',
    vat: ''
  });
  
  // New contact form data
  const [newContactData, setNewContactData] = useState({
    name: '',
    email: '',
    phone: '',
    function: '' // Job title
  });
  
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Load klanten (customers) per geselecteerd bedrijf – opnieuw laden bij wissel van bedrijf
  useEffect(() => {
    setSelectedCustomer(null);
    setSelectedContact(null);
    loadCustomers();
  }, [selectedSubscriberId]);

  // Pre-fill if initial client name provided
  useEffect(() => {
    const list = Array.isArray(customers) ? customers : [];
    if (initialClientName && list.length > 0) {
      const match = list.find(c => 
        c.name?.toLowerCase().includes(initialClientName.toLowerCase())
      );
      if (match) {
        setSelectedCustomer(match);
      }
    }
  }, [initialClientName, customers]);

  // Pre-fill new customer form (en optioneel contact) uit inkomende offerte-aanvraag (API)
  // Respecteert de subscriber-instelling api_customer_type: bij 'particulier' worden
  // contactgegevens direct als particuliere klant ingevuld i.p.v. als bedrijf+contactpersoon.
  useEffect(() => {
    if (initialCustomerFromRequest && typeof initialCustomerFromRequest === 'object') {
      const c = initialCustomerFromRequest;
      setIsNewCustomer(true);
      setSelectedCustomer(null);

      // Haal api_customer_type op uit subscriber settings
      const fetchTypeAndPrefill = async () => {
        let apiCustomerType = 'bedrijf';
        if (selectedSubscriberId) {
          try {
            const res = await api.get(`/api/subscribers/${selectedSubscriberId}`);
            apiCustomerType = res.data?.company_details?.api_customer_type || 'bedrijf';
          } catch (_) {}
        }

        const hasCompanyFields = c.name || c.email || c.phone || c.vat;
        const hasContactFields = c.contact_name || c.contact_email || c.contact_phone;

        if (apiCustomerType === 'particulier' && !hasCompanyFields && hasContactFields) {
          // Particulier: contactgegevens worden de klantgegevens
          setNewCustomerData(prev => ({
            ...prev,
            customerType: 'particulier',
            name: c.contact_name || '',
            email: c.contact_email || '',
            phone: c.contact_phone || '',
            street: c.street || '',
            city: c.city || '',
            zip: c.zip || ''
          }));
          setIsNewContact(false);
        } else {
          // Bedrijf (standaard): bedrijfsvelden + apart contactpersoon
          setNewCustomerData(prev => ({
            ...prev,
            customerType: apiCustomerType === 'particulier' ? 'particulier' : 'bedrijf',
            name: c.name || '',
            email: c.email || '',
            phone: c.phone || '',
            street: c.street || '',
            city: c.city || '',
            zip: c.zip || '',
            vat: c.vat || ''
          }));
          if (hasContactFields) {
            setNewContactData(prev => ({
              ...prev,
              name: c.contact_name || prev.name,
              email: c.contact_email || prev.email,
              phone: c.contact_phone || prev.phone
            }));
            setIsNewContact(true);
          }
        }
      };
      fetchTypeAndPrefill();
    }
  }, [initialCustomerFromRequest, selectedSubscriberId]);

  // Load contacts when klant is selected
  useEffect(() => {
    if (selectedCustomer && selectedCustomer.odoo_partner_id) {
      loadContactsForCustomer(selectedCustomer.odoo_partner_id);
    }
  }, [selectedCustomer]);

  const loadCustomers = async () => {
    setLoadingCustomers(true);
    try {
      // Load from Supabase (synced customers)
      const response = await api.get('/api/customers');
      setCustomers(Array.isArray(response.data) ? response.data : []);
      
      if (response.data?.length === 0) {
        setError('Geen klanten gevonden. Klik eerst op "👥 Sync klanten" in de header.');
      }
    } catch (err) {
      console.error('Error loading customers:', err);
      setError('Kon klanten niet laden. Synchroniseer eerst klanten via de header.');
    } finally {
      setLoadingCustomers(false);
    }
  };

  // Zoek in dezelfde klantenlijst als de dropdown (Supabase), zodat we altijd dezelfde klanten vinden
  useEffect(() => {
    if (!searchModalOpen) return;
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    const q = (searchQuery && String(searchQuery).trim()).toLowerCase() || '';
    if (!q) {
      setSearchResults([]);
      setSearchLoading(false);
      return;
    }
    setSearchLoading(true);
    searchDebounceRef.current = setTimeout(() => {
      const list = Array.isArray(customers) ? customers : [];
      const filtered = list.filter((c) => {
        const name = (c.name || '').toLowerCase();
        const email = (c.email || '').toLowerCase();
        return name.includes(q) || email.includes(q);
      });
      setSearchResults(filtered);
      setSearchLoading(false);
      searchDebounceRef.current = null;
    }, 200);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [searchModalOpen, searchQuery, customers]);

  const openSearchModal = () => {
    setSearchModalOpen(true);
    setSearchQuery('');
    setSearchResults([]);
  };

  const closeSearchModal = () => {
    setSearchModalOpen(false);
    setSearchQuery('');
    setSearchResults([]);
  };

  const selectCustomerFromList = (customer) => {
    setSelectedCustomer(customer);
    setIsNewCustomer(false);
    setContacts([]);
    closeSearchModal();
  };

  const loadContactsForCustomer = async (odooPartnerId) => {
    setLoadingContacts(true);
    setContacts([]);
    setSelectedContact(null);
    setError(null);
    try {
      const response = await api.get(`/api/odoo/contacts/${odooPartnerId}`);
      setContacts(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Error loading contacts:', err);
      if (err.code === 'ERR_NETWORK' || err.message?.includes('Network')) {
        setError('Backend niet bereikbaar. Start de server met: npm start');
      } else {
        setError(err.response?.data?.message || err.response?.data?.error || 'Kon contactpersonen niet laden uit Odoo.');
        setContacts([]);
      }
    } finally {
      setLoadingContacts(false);
    }
  };

  const handleCustomerSelect = (customerId) => {
    if (customerId === 'new') {
      setIsNewCustomer(true);
      setSelectedCustomer(null);
      setContacts([]);
      setIsNewContact(false);
    } else {
      setIsNewCustomer(false);
      const list = Array.isArray(customers) ? customers : [];
      const customer = list.find(c => String(c.id) === String(customerId));
      setSelectedCustomer(customer || null);
    }
  };

  const handleContactSelect = (contactId) => {
    if (contactId === 'new') {
      setIsNewContact(true);
      setSelectedContact(null);
    } else {
      setIsNewContact(false);
      if (contactId === '' || contactId === undefined) {
        setSelectedContact(null);
        return;
      }
      const contactList = Array.isArray(contacts) ? contacts : [];
      const contact = contactList.find(c => c.id === parseInt(contactId, 10));
      setSelectedContact(contact || null);
    }
  };

  const handleNewCustomerChange = (field, value) => {
    setNewCustomerData(prev => ({ ...prev, [field]: value }));
  };

  const handleNewContactChange = (field, value) => {
    setNewContactData(prev => ({ ...prev, [field]: value }));
  };

  const createCustomerInOdoo = async () => {
    try {
      const isCompany = newCustomerData.customerType === 'bedrijf';
      const response = await api.post('/api/odoo/companies', {
        ...newCustomerData,
        is_company: isCompany
      });
      setSuccess(`Klant "${newCustomerData.name}" aangemaakt in Odoo`);
      
      await loadCustomers();
      setIsNewCustomer(false);
      
      return response.data;
    } catch (err) {
      setError('Kon klant niet aanmaken in Odoo: ' + (err.response?.data?.error || err.message));
      throw err;
    }
  };

  const createContactInOdoo = async () => {
    const parentId = selectedCustomer?.odoo_partner_id;
    if (!parentId) {
      setError('Selecteer eerst een klant (met Odoo koppeling)');
      return;
    }
    
    try {
      const response = await api.post('/api/odoo/contacts', {
        ...newContactData,
        parent_id: parentId
      });
      setSuccess(`Contact "${newContactData.name}" aangemaakt in Odoo`);
      
      await loadContactsForCustomer(parentId);
      setIsNewContact(false);
      
      return response.data;
    } catch (err) {
      setError('Kon contact niet aanmaken in Odoo: ' + (err.response?.data?.error || err.message));
      throw err;
    }
  };

  const handleContinue = async () => {
    setError(null);
    
    if (!selectedCustomer && !isNewCustomer) {
      setError('Selecteer een klant of maak een nieuwe klant aan');
      return;
    }
    
    let customerData = selectedCustomer;
    let contactData = selectedContact;
    
    if (isNewCustomer) {
      if (!newCustomerData.name.trim()) {
        setError('Vul de klantnaam in');
        return;
      }
      try {
        const created = await createCustomerInOdoo();
        customerData = { 
          company_name: newCustomerData.name,
          name: newCustomerData.name,
          odoo_partner_id: created.id,
          company_details: newCustomerData
        };
      } catch {
        return;
      }
    }
    
    const parentIdForContact = (selectedCustomer?.odoo_partner_id ?? (isNewCustomer ? customerData?.odoo_partner_id : null)) || null;
    if (isNewContact && parentIdForContact && newContactData.name.trim()) {
      try {
        const response = await api.post('/api/odoo/contacts', {
          ...newContactData,
          parent_id: parentIdForContact
        });
        const created = response.data;
        contactData = {
          id: created.id,
          name: newContactData.name,
          email: newContactData.email,
          phone: newContactData.phone,
          function: newContactData.function
        };
      } catch (err) {
        setError('Kon contact niet aanmaken in Odoo: ' + (err.response?.data?.error || err.message));
        return;
      }
    }
    
    const clientDataString = buildClientDataString(customerData, contactData);

    try {
      onComplete({
        selectedCompany: customerData,
        selectedCustomer: customerData,
        selectedContact: contactData,
        clientName: customerData?.name || customerData?.company_name || newCustomerData.name,
        clientData: clientDataString,
        isNewCustomer,
        isNewContact
      });
    } catch (err) {
      console.error('onComplete error:', err);
      setError('Kon niet doorgaan. Probeer opnieuw of kies "Doorgaan zonder klant".');
    }
  };

  const handleContinueWithoutCustomer = () => {
    setError(null);
    try {
      onComplete({
        selectedCompany: null,
        selectedCustomer: null,
        selectedContact: null,
        clientName: '',
        clientData: '',
        isNewCustomer: false,
        isNewContact: false
      });
    } catch (err) {
      console.error('onComplete error:', err);
      setError('Kon niet doorgaan. Probeer de pagina te vernieuwen.');
    }
  };

  const buildClientDataString = (customer, contact) => {
    const lines = [];
    const name = customer?.name || customer?.company_name || '';
    if (name) lines.push(name);

    if (contact?.name) {
      lines.push(`t.a.v. ${contact.name}`);
    }

    const details = customer?.company_details || customer?.address || {};
    const street = customer?.street || details.street;
    const city = customer?.city || details.city;
    const zip = customer?.zip || details.zip;
    const country = customer?.country || details.country;
    if (street) lines.push(street);
    if (zip && city) {
      lines.push(`${zip} ${city}`);
    } else if (city) {
      lines.push(city);
    }
    if (country) lines.push(country);

    const phone = contact?.phone || customer?.phone || details.phone;
    if (phone) lines.push(`Tel: ${phone}`);
    const email = contact?.email || customer?.email || details.email;
    if (email) lines.push(`E-mail: ${email}`);

    const vat = customer?.vat || details.vat;
    if (vat) lines.push(`BTW: ${vat}`);
    const kvk = customer?.kvk || details.kvk;
    if (kvk) lines.push(`KvK: ${kvk}`);

    const website = customer?.website || details.website;
    if (website) lines.push(website);

    return lines.filter(l => l).join('\n');
  };

  return (
    <div className="client-contact-selection">
      <div className="container">
        <h2>Stap 4: Klant & Contact Selecteren</h2>
        
        {error && <div className="error">{error}</div>}
        {success && <div className="success">{success}</div>}

        {/* Klant Selection (1 van de 204) */}
        <div className="card">
          <h3>Klant</h3>
          
          {loadingCustomers ? (
            <div className="loading">Klanten laden van Odoo...</div>
          ) : (
            <>
              <div className="input-group" data-demo-step="demo-step-3">
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  Selecteer klant
                  <button
                    type="button"
                    className="btn btn-secondary"
                    style={{ fontSize: 13, padding: '6px 12px' }}
                    onClick={openSearchModal}
                  >
                    🔍 Zoek klant in venster
                  </button>
                </label>
                <select 
                  value={isNewCustomer ? 'new' : (selectedCustomer?.id ?? '')}
                  onChange={(e) => handleCustomerSelect(e.target.value)}
                >
                  <option value="">-- Selecteer een klant --</option>
                  <option value="new">+ Nieuwe klant aanmaken</option>
                  {(Array.isArray(customers) ? customers : []).map(customer => (
                    <option key={customer.id} value={customer.id}>
                      {customer.name}
                    </option>
                  ))}
                </select>
              </div>
              
              {selectedCustomer && (
                <div className="selected-info">
                  <strong>{selectedCustomer.name}</strong>
                  {(selectedCustomer.email || selectedCustomer.phone) && (
                    <div className="details">
                      {selectedCustomer.email && <span>{selectedCustomer.email}</span>}
                      {selectedCustomer.phone && <span>{selectedCustomer.phone}</span>}
                    </div>
                  )}
                </div>
              )}
              
              {isNewCustomer && (
                <div className="new-form">
                  <h4>Nieuwe klant aanmaken</h4>
                  <div className="input-group" style={{ marginBottom: 16 }}>
                    <label>Type klant</label>
                    <div className="customer-type-options">
                      <label className="radio-option">
                        <input
                          type="radio"
                          name="customerType"
                          checked={newCustomerData.customerType === 'bedrijf'}
                          onChange={() => handleNewCustomerChange('customerType', 'bedrijf')}
                        />
                        <span>Bedrijf</span>
                      </label>
                      <label className="radio-option">
                        <input
                          type="radio"
                          name="customerType"
                          checked={newCustomerData.customerType === 'particulier'}
                          onChange={() => {
                            handleNewCustomerChange('customerType', 'particulier');
                            setIsNewContact(false);
                          }}
                        />
                        <span>Particulier</span>
                      </label>
                    </div>
                    {newCustomerData.customerType === 'bedrijf' && (
                      <p className="form-hint">Bij een bedrijf kunt u daarna een contactpersoon toevoegen.</p>
                    )}
                  </div>
                  <div className="form-grid">
                    <div className="input-group">
                      <label>{newCustomerData.customerType === 'bedrijf' ? 'Bedrijfsnaam *' : 'Naam *'}</label>
                      <input
                        type="text"
                        value={newCustomerData.name}
                        onChange={(e) => handleNewCustomerChange('name', e.target.value)}
                        placeholder={newCustomerData.customerType === 'bedrijf' ? 'Bedrijfsnaam' : 'Voor- en achternaam'}
                        autoFocus
                      />
                    </div>
                    <div className="input-group">
                      <label>E-mail</label>
                      <input
                        type="email"
                        value={newCustomerData.email}
                        onChange={(e) => handleNewCustomerChange('email', e.target.value)}
                        placeholder="info@bedrijf.nl"
                      />
                    </div>
                    <div className="input-group">
                      <label>Telefoon</label>
                      <input
                        type="tel"
                        value={newCustomerData.phone}
                        onChange={(e) => handleNewCustomerChange('phone', e.target.value)}
                        placeholder="+31 20 1234567"
                      />
                    </div>
                    <div className="input-group">
                      <label>Straat</label>
                      <input
                        type="text"
                        value={newCustomerData.street}
                        onChange={(e) => handleNewCustomerChange('street', e.target.value)}
                        placeholder="Straatnaam 123"
                      />
                    </div>
                    <div className="input-group">
                      <label>Postcode</label>
                      <input
                        type="text"
                        value={newCustomerData.zip}
                        onChange={(e) => handleNewCustomerChange('zip', e.target.value)}
                        placeholder="1234 AB"
                      />
                    </div>
                    <div className="input-group">
                      <label>Plaats</label>
                      <input
                        type="text"
                        value={newCustomerData.city}
                        onChange={(e) => handleNewCustomerChange('city', e.target.value)}
                        placeholder="Amsterdam"
                      />
                    </div>
                    {newCustomerData.customerType === 'bedrijf' && (
                      <div className="input-group">
                        <label>BTW-nummer</label>
                        <input
                          type="text"
                          value={newCustomerData.vat}
                          onChange={(e) => handleNewCustomerChange('vat', e.target.value)}
                          placeholder="NL123456789B01"
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Contact Selection - only when existing company or new customer type "bedrijf" */}
        {(selectedCustomer || (isNewCustomer && newCustomerData.customerType === 'bedrijf')) && (
          <div className="card">
            <h3>Contactpersoon</h3>
            
            {loadingContacts ? (
              <div className="loading">Contacten laden van Odoo...</div>
            ) : (
              <>
                {contacts.length > 0 && (
                  <div className="input-group">
                    <label>Selecteer contactpersoon</label>
                    <select
                      value={isNewContact ? 'new' : (selectedContact?.id || '')}
                      onChange={(e) => handleContactSelect(e.target.value)}
                    >
                      <option value="">-- Geen contact / Direct naar bedrijf --</option>
                      {(Array.isArray(contacts) ? contacts : []).map(contact => (
                        <option key={contact.id} value={contact.id}>
                          {contact.name} {contact.function ? `(${contact.function})` : ''}
                        </option>
                      ))}
                      <option value="new">+ Nieuw contact aanmaken</option>
                    </select>
                  </div>
                )}
                
                {contacts.length === 0 && !loadingContacts && !isNewCustomer && (
                  <div className="info-message">
                    Geen contactpersonen gevonden voor deze klant.
                    <button 
                      className="btn btn-link"
                      onClick={() => setIsNewContact(true)}
                    >
                      + Nieuw contact aanmaken
                    </button>
                  </div>
                )}
                
                {/* Selected contact info */}
                {selectedContact && (
                  <div className="selected-info">
                    <strong>{selectedContact.name}</strong>
                    <div className="details">
                      {selectedContact.function && <span>{selectedContact.function}</span>}
                      {selectedContact.email && <span>{selectedContact.email}</span>}
                      {selectedContact.phone && <span>{selectedContact.phone}</span>}
                    </div>
                  </div>
                )}
                
                {/* New contact form: bij bestaande klant (geen contacten) of bij nieuwe klant type bedrijf */}
                {(isNewContact || (contacts.length === 0 && !loadingContacts && selectedCustomer) || (isNewCustomer && newCustomerData.customerType === 'bedrijf')) && (
                  <div className="new-form">
                    <h4>Nieuw contact aanmaken</h4>
                    {isNewCustomer && newCustomerData.customerType === 'bedrijf' && (
                      <p className="form-hint" style={{ marginBottom: 12 }}>Optioneel: vul een contactpersoon in. Laat leeg om alleen het bedrijf op te slaan.</p>
                    )}
                    <div className="form-grid">
                      <div className="input-group">
                        <label>Naam *</label>
                        <input
                          type="text"
                          value={newContactData.name}
                          onChange={(e) => handleNewContactChange('name', e.target.value)}
                          placeholder="Voornaam Achternaam"
                        />
                      </div>
                      <div className="input-group">
                        <label>E-mail</label>
                        <input
                          type="email"
                          value={newContactData.email}
                          onChange={(e) => handleNewContactChange('email', e.target.value)}
                          placeholder="contact@bedrijf.nl"
                        />
                      </div>
                      <div className="input-group">
                        <label>Telefoon</label>
                        <input
                          type="tel"
                          value={newContactData.phone}
                          onChange={(e) => handleNewContactChange('phone', e.target.value)}
                          placeholder="+31 6 12345678"
                        />
                      </div>
                      <div className="input-group">
                        <label>Functie</label>
                        <input
                          type="text"
                          value={newContactData.function}
                          onChange={(e) => handleNewContactChange('function', e.target.value)}
                          placeholder="Directeur, Manager, etc."
                        />
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Continue buttons */}
        <div className="action-buttons">
          <button
            className="btn btn-primary"
            onClick={handleContinue}
            disabled={loadingCustomers || loadingContacts}
          >
            Doorgaan naar Offerte
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleContinueWithoutCustomer}
            disabled={loadingCustomers}
          >
            Doorgaan zonder klant (invullen in volgende stap)
          </button>
        </div>
      </div>

      {/* Zoek klant modal */}
      {searchModalOpen && (
        <div className="client-search-overlay" onClick={closeSearchModal}>
          <div className="client-search-modal" onClick={(e) => e.stopPropagation()}>
            <div className="client-search-modal-header">
              <h3>Klant zoeken</h3>
              <button type="button" className="client-search-close" onClick={closeSearchModal} aria-label="Sluiten">×</button>
            </div>
            <div className="client-search-modal-body">
              <input
                type="text"
                className="client-search-input"
                placeholder="Typ bedrijfsnaam of naam..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoFocus
              />
              {searchLoading && <div className="client-search-loading">Zoeken...</div>}
              {!searchLoading && searchQuery.trim() && searchResults.length === 0 && (
                <div className="client-search-empty">Geen klanten gevonden. Probeer een andere zoekterm.</div>
              )}
              {!searchLoading && searchResults.length > 0 && (
                <ul className="client-search-results">
                  {(Array.isArray(searchResults) ? searchResults : []).map((customer) => (
                    <li key={customer.id}>
                      <button
                        type="button"
                        className="client-search-result-item"
                        onClick={() => selectCustomerFromList(customer)}
                      >
                        <strong>{customer.name}</strong>
                        {(customer.email || customer.phone) && (
                          <span className="client-search-result-meta">
                            {[customer.email, customer.phone].filter(Boolean).join(' · ')}
                          </span>
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientContactSelection;
